// In Next.js, this file would be called: app/providers.tsx
"use client";

// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import {
  defaultShouldDehydrateQuery,
  isServer,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { PostHogProvider } from "posthog-js/react";
import { Toaster } from "@/components/ui/toaster";
import { UserMedia } from "../ui/user-media";
import { getQueryClient } from "../get-query-client";
import PostHogPageView from "@/components/PostHogPageView";
import posthog from "posthog-js";
import { useEffect } from "react";
import { Locale, LocaleProvider } from "../hooks/locale";

export default function Providers({
  children,
  locale,
}: {
  children: React.ReactNode;
  locale: Locale;
}) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  useEffect(() => {
    console.log("Attempting 🦔");
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY as string;
    const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
    if (!posthogKey || !posthogHost) {
      console.warn("🦔 PostHog key or host not found");
      return;
    }
    posthog.init(posthogKey, {
      api_host: posthogHost, //"/ingest",

      //ui_host: "https://us.posthog.com",
      loaded: (posthog) => {
        // console.log("🦔 loaded callback triggered");
        if (process.env.NODE_ENV === "development") {
          console.log(
            "Development environment detected, disabling 🦔 capturing",
          );
          posthog.opt_out_capturing();
          posthog.set_config({ disable_session_recording: true });
        }
      },
      person_profiles: "always",
      persistence: "cookie",
      //xhr_headers: { "Should-Proxy": "true" },
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
    });
    console.log("🦔 init");
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <PostHogProvider client={posthog}>
        <LocaleProvider initialLocale={locale}>
          <PostHogPageView />
          <UserMedia>{children}</UserMedia>
          <Toaster />
        </LocaleProvider>
      </PostHogProvider>
    </QueryClientProvider>
  );
}
